import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { ErrorView } from '../components/Error'
import { Footer } from '../components/Footer'
import { hasNoReviews, NoReviewsYetBadge } from '../components/NoReviewsYetBadge'
import { Paging } from '../components/Paging'
import { Spinner } from '../components/Spinner'
import { StarRating } from '../components/StarRating'
import { TopLogo } from '../components/TopLogo'
import { useAddMissingOrganizationModal } from '../hooks/useAddMissingOrganizationModal'
import { Agency } from '../models/Agency.model'
import { Hospital } from '../models/Hospital.model'
import { USStates } from '../models/USStates.model'
import { useAuthToken } from '../providers/AuthStateContext'
import { AgencyService } from '../services/AgencyService/AgencyService'
import { FeaturedReview, FeaturedReviewsService } from '../services/FeaturedReviewsService/FeaturedReviewsService'
import { HospitalService } from '../services/HospitalService/HospitalService'
import { addHashParamToUrl, checkIsUpTree, getHashParamValueFromUrl, scrollIntoView } from '../utils'
import {
  buildDirectorySearchLocation,
  consumeDirectoryScrollRestore,
  type DirectoryReturnLocation,
  directorySearchLocationMatches,
  getDirectoryDetailLinkState,
  parseDirectorySearchFromLocation,
  saveDirectoryScrollPosition,
} from '../utils/directorySearchUrl'
import type { DirectorySearchLocationState } from './DirectorySearch'

type DirectoryDetailNavigation = {
  directoryReturnTo: DirectoryReturnLocation
  onNavigateToDetail: () => void
}

const DIRECTORY_SEARCH_MAX_WIDTH_PX = 767

function useNarrowDirectorySearchViewport() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${DIRECTORY_SEARCH_MAX_WIDTH_PX}px)`).matches
      : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${DIRECTORY_SEARCH_MAX_WIDTH_PX}px)`)
    const onChange = () => setNarrow(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return narrow
}

//-----------------------------------------------------------------------------
// SUB COMPONENTS
//-----------------------------------------------------------------------------

const featuredReviewsService = new FeaturedReviewsService()

const formatFeaturedReviewMeta = (item: FeaturedReview): string =>
  [
    item.reviewerPosition,
    item.reviewerTitle && item.reviewerTitle !== 'null' ? item.reviewerTitle : undefined,
    item.hospital.city && item.hospital.state
      ? `${item.hospital.city}, ${item.hospital.state}`
      : (item.hospital.state ?? undefined),
  ]
    .filter(Boolean)
    .join(' · ')

const RecentReviewsShowcase = () => {
  const [reviews, setReviews] = useState<FeaturedReview[]>([])
  const history = useHistory()

  useEffect(() => {
    featuredReviewsService
      .getFeaturedReviews()
      .then((res) => {
        if (res.reviews?.length) setReviews(res.reviews)
      })
      .catch(() => {
        // Silently fail — section stays hidden
      })
  }, [])

  if (!reviews.length) return null

  return (
    <div className="mx-auto mb-10 max-w-3xl md:mb-12 lg:max-w-4xl xl:max-w-5xl">
      <h2 className="text-center text-xs font-bold uppercase tracking-widest text-gray-500 mb-5">Recent reviews</h2>
      <ul className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
        {reviews.map((item) => {
          const meta = formatFeaturedReviewMeta(item)
          return (
            <li
              key={item.reviewId}
              className="flex min-h-[7.25rem] cursor-pointer flex-col rounded-xl bg-gray-100 p-4 text-left transition-colors hover:bg-gray-200 sm:min-h-[7.5rem]"
              onClick={() => history.push(`/hospitals/${item.hospital.permalink}`)}
            >
              <div className="mb-2">
                <StarRating rating={item.rating} size="sm" />
              </div>
              <p className="mb-2 line-clamp-2 min-h-[2.5rem] text-sm leading-snug text-graphite-grey md:min-h-[2.75rem] md:text-[15px]">
                &ldquo;{item.feedbackText}&rdquo;
              </p>
              {meta ? <p className="mt-auto line-clamp-1 text-xs text-gray-500 md:text-sm">{meta}</p> : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
type FilterProps = {
  currentSearchTerm: string
  setNewSearchTerm: (newTerm: string) => void
  setAutoCompleteTerm: React.Dispatch<React.SetStateAction<string>>
  autoCompleteResults: Hospital[] | Agency[]
  activeTab: 'hospitals' | 'agencies'
}
const Filter = ({
  currentSearchTerm,
  setNewSearchTerm,
  setAutoCompleteTerm,
  autoCompleteResults,
  activeTab,
}: FilterProps) => {
  const [termFocused, setTermFocused] = useState(false)
  const [tempSearchTerm, setTempSearchTerm] = useState(currentSearchTerm)
  const autoCompleteContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTempSearchTerm(currentSearchTerm)
  }, [currentSearchTerm])

  const runSearch = () => {
    setNewSearchTerm(tempSearchTerm)
    setTermFocused(false)
  }

  return (
    <div className="relative z-20 mx-auto w-full max-w-2xl">
      <div className="flex flex-row items-center rounded-full bg-white p-1.5 pl-4 shadow-[0_4px_28px_-6px_rgba(0,0,0,0.14)] ring-1 ring-black/[0.06] sm:pl-6">
        <input
          value={tempSearchTerm}
          onChange={(evt) => setTempSearchTerm(evt.target.value)}
          onKeyDown={(evt) => {
            if (evt.key === 'Enter') {
              evt.preventDefault()
              runSearch()
            }
          }}
          onKeyUp={(evt) => {
            if (evt.key !== 'Enter') {
              setAutoCompleteTerm(tempSearchTerm)
              setTermFocused(true)
            }
          }}
          onFocus={(evt) => {
            const el = evt.target as HTMLElement
            el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
          }}
          onBlur={(evt) => {
            if (!checkIsUpTree(evt.relatedTarget, autoCompleteContainerRef.current)) {
              setTermFocused(false)
            }
          }}
          className="min-w-0 flex-1 border-0 bg-transparent py-2.5 pr-2 text-base text-graphite-grey placeholder:text-gray-500 focus:outline-none focus:ring-0 sm:py-3 sm:pr-3 sm:text-[17px]"
          placeholder="Search hospitals or agencies..."
          aria-label={activeTab === 'hospitals' ? 'Search hospitals' : 'Search agencies'}
        />
        <button
          type="button"
          className="mr-1.5 flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-orange/90 sm:mr-2 sm:min-h-0 sm:px-7 sm:py-3 sm:text-base"
          onMouseDown={(e) => e.preventDefault()}
          onClick={runSearch}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 sm:h-5 sm:w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          <span>Search</span>
        </button>
      </div>
      {autoCompleteResults.length > 0 && termFocused && (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-2 max-h-60 overflow-y-auto rounded-xl border border-gray-100 bg-white shadow-2xl"
          ref={autoCompleteContainerRef}
        >
          {autoCompleteResults.map((item) => (
            <Link
              key={item.id}
              className="flex items-center justify-between gap-3 border-b border-gray-100 p-3 text-base text-graphite-grey transition-colors last:border-0 hover:bg-gray-50"
              to={
                activeTab === 'hospitals'
                  ? `/hospitals/${(item as Hospital).permalink}`
                  : `/agencies/${(item as Agency).permalink}`
              }
              tabIndex={0}
            >
              <span className="min-w-0 flex-1" dangerouslySetInnerHTML={{ __html: item.name }} />
              {activeTab === 'hospitals' && hasNoReviews((item as Hospital).reviewCount) ? <NoReviewsYetBadge /> : null}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

const SORT_OPTIONS: readonly { label: string; value: string }[] = [
  { label: 'A - Z', value: 'az' },
  { label: 'Most Reviewed', value: 'mostReviewed' },
  { label: 'Newest', value: 'newest' },
  { label: 'Least Reviewed', value: 'leastReviewed' },
  { label: 'Top Rated', value: 'highRating' },
  { label: 'Lowest Rated', value: 'lowRating' },
  { label: 'Z - A', value: 'za' },
]

type SortProps = {
  sortValue: string
  setSortValue: (sortValue: string) => void
  totalResults: number
  numberShowing: number
  page: number
  resultCategory: 'hospitals' | 'agencies'
}
const Sort = ({ sortValue, setSortValue, totalResults, numberShowing, page, resultCategory }: SortProps) => {
  const from = (page - 1) * 12 + (numberShowing > 0 ? 1 : 0)
  const to = (page - 1) * 12 + numberShowing
  const entityNoun =
    resultCategory === 'hospitals'
      ? totalResults === 1
        ? 'hospital'
        : 'hospitals'
      : totalResults === 1
        ? 'agency'
        : 'agencies'
  const [sortMenuOpen, setSortMenuOpen] = useState(false)
  const sortRootRef = useRef<HTMLDivElement>(null)
  const sortLabelId = useId()
  const sortListboxId = useId()

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sortValue)?.label ?? sortValue

  useEffect(() => {
    if (!sortMenuOpen) return
    const close = () => setSortMenuOpen(false)
    const onDocPointer = (e: MouseEvent | TouchEvent) => {
      if (sortRootRef.current?.contains(e.target as Node)) return
      close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDocPointer)
    document.addEventListener('touchstart', onDocPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocPointer)
      document.removeEventListener('touchstart', onDocPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [sortMenuOpen])

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
      {totalResults > 0 ? (
        <div className="order-2 sm:order-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">Results</p>
          <p className="mt-1 text-sm text-gray-600">
            Showing{' '}
            <span className="font-semibold tabular-nums text-graphite-grey">
              {from}–{to}
            </span>{' '}
            of <span className="font-semibold tabular-nums text-graphite-grey">{totalResults.toLocaleString()}</span>{' '}
            {entityNoun}
          </p>
        </div>
      ) : (
        <span className="order-2 sm:order-1" />
      )}

      <div className="order-1 flex w-full flex-col gap-1.5 sm:order-2 sm:w-auto sm:min-w-[240px] lg:min-w-[260px]">
        <span id={sortLabelId} className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">
          Sort by
        </span>
        <div ref={sortRootRef} className="relative">
          <button
            type="button"
            className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl border bg-white py-3 pl-4 pr-3 text-left text-sm font-medium text-graphite-grey shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 lg:py-2.5 ${
              sortMenuOpen ? 'border-gray-300' : 'border-gray-200/90 hover:border-gray-300'
            }`}
            aria-expanded={sortMenuOpen}
            aria-controls={sortListboxId}
            aria-haspopup="listbox"
            aria-labelledby={sortLabelId}
            onClick={() => setSortMenuOpen((o) => !o)}
          >
            <span className="min-w-0 truncate">{currentSortLabel}</span>
            <span className="pointer-events-none flex shrink-0 text-gray-500" aria-hidden>
              <svg
                className={`h-4 w-4 transition-transform ${sortMenuOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
          {sortMenuOpen ? (
            <ul
              id={sortListboxId}
              role="listbox"
              className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(20rem,calc(100vh-8rem))] overflow-auto rounded-xl border border-gray-200/90 bg-white py-1 text-sm shadow-md"
            >
              {SORT_OPTIONS.map((opt) => {
                const selected = opt.value === sortValue
                return (
                  <li key={opt.value} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`flex w-full items-center gap-2 px-4 py-2.5 text-left font-medium transition-colors ${
                        selected ? 'bg-gray-100 text-graphite-grey' : 'text-graphite-grey hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSortValue(opt.value)
                        setSortMenuOpen(false)
                      }}
                    >
                      <span className="flex w-5 shrink-0 justify-center text-gray-600" aria-hidden>
                        {selected ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : null}
                      </span>
                      <span className="min-w-0">{opt.label}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  )
}

// TODO: add link for hospital reviews subsection of hospital detail page on read reviews (current site uses hashtag scroll)
const cardPrimaryButtonClass =
  'flex min-h-[44px] items-center justify-center rounded-xl bg-orange text-center text-sm font-semibold text-white shadow-sm transition-opacity hover:bg-opacity-90'
const cardSecondaryButtonClass =
  'flex min-h-[44px] items-center justify-center rounded-xl border-2 border-aqua-mint/50 bg-white text-center text-sm font-semibold text-aqua-mint shadow-sm transition-colors hover:border-aqua-mint hover:bg-aqua-mint/8'

function formatBrowseDirectoryLabel(reviewCount?: number | null): string {
  if (!reviewCount || reviewCount === 0) {
    return 'View profile'
  }

  return `Read ${reviewCount} review${reviewCount === 1 ? '' : 's'}`
}

const WRITE_REVIEW_LABEL = 'Write a review'

function cardButtonLayoutClass(isRow: boolean, isPrimary: boolean): string {
  const width = isRow ? 'flex-1 px-3' : 'w-full px-4 py-2.5'
  return `${isPrimary ? cardPrimaryButtonClass : cardSecondaryButtonClass} ${width}`
}

const hospitalCardActions = (
  hospital: Hospital,
  layout: 'row' | 'stack',
  detailNavigation: DirectoryDetailNavigation,
) => {
  const isRow = layout === 'row'
  const hospitalDetailTo = {
    pathname: `/hospitals/${hospital.permalink}`,
    state: getDirectoryDetailLinkState(detailNavigation.directoryReturnTo),
  }

  return (
    <div className={isRow ? 'mt-4 flex gap-2 lg:hidden' : 'hidden flex-col gap-2 lg:flex lg:w-[10rem] xl:w-[11rem]'}>
      <Link
        to={hospitalDetailTo}
        onClick={detailNavigation.onNavigateToDetail}
        className={cardButtonLayoutClass(isRow, true)}
      >
        {formatBrowseDirectoryLabel(hospital.reviewCount)}
      </Link>
      <Link to={`/review-hospital/${hospital.id}`} className={cardButtonLayoutClass(isRow, false)}>
        {WRITE_REVIEW_LABEL}
      </Link>
    </div>
  )
}

const HospitalListItem = ({
  hospital,
  detailNavigation,
}: {
  hospital: Hospital
  detailNavigation: DirectoryDetailNavigation
}) => {
  const hospitalDetailTo = {
    pathname: `/hospitals/${hospital.permalink}`,
    state: getDirectoryDetailLinkState(detailNavigation.directoryReturnTo),
  }

  return (
    <li>
      <article className="rounded-2xl border border-gray-100/90 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.03] transition-all duration-200 hover:border-gray-200 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] sm:p-5 lg:p-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-0 lg:p-5">
          <div className="flex min-w-0 flex-1 items-start gap-4 sm:gap-5 lg:items-center lg:gap-6 lg:pr-8">
            <Link
              to={hospitalDetailTo}
              onClick={detailNavigation.onNavigateToDetail}
              className="relative shrink-0 self-start overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-gray-100 ring-1 ring-gray-100/80 shadow-sm lg:self-center"
            >
              <div className="flex size-[4.5rem] items-center justify-center sm:size-24 lg:size-[6.25rem]">
                <img
                  className="size-[3.25rem] object-contain p-0.5 sm:size-16 lg:size-[5rem]"
                  src={hospital.image ? hospital.image : undefined}
                  alt=""
                />
              </div>
            </Link>

            <div className="flex min-w-0 flex-1 flex-col lg:max-w-none lg:py-0.5">
              <Link
                to={hospitalDetailTo}
                onClick={detailNavigation.onNavigateToDetail}
                className="text-base font-semibold leading-snug text-graphite-grey visited:text-graphite-grey hover:text-royal-indigo focus-visible:rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-indigo/35 sm:text-lg"
              >
                <span className="line-clamp-2">{hospital.name}</span>
              </Link>

              <div className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <div className="min-w-0 leading-relaxed">
                  {hospital.address ? <div className="text-gray-700">{hospital.address}</div> : null}
                  <div className="text-gray-600">
                    {hospital.city}, {hospital.state.code}
                  </div>
                </div>
              </div>

              {hospital.rating > 0 && !hasNoReviews(hospital.reviewCount) ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StarRating rating={hospital.rating} size="sm" />
                  <span className="text-sm font-semibold tabular-nums text-graphite-grey">
                    {typeof hospital.rating === 'number'
                      ? hospital.rating.toFixed(1)
                      : Number(hospital.rating || 0).toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({hospital.reviewCount} review{hospital.reviewCount && hospital.reviewCount > 1 ? 's' : ''})
                  </span>
                </div>
              ) : hasNoReviews(hospital.reviewCount) ? (
                <div className="mt-3">
                  <NoReviewsYetBadge />
                </div>
              ) : null}

              {hospitalCardActions(hospital, 'row', detailNavigation)}
            </div>
          </div>

          <div className="hidden border-gray-100 lg:flex lg:shrink-0 lg:flex-col lg:justify-center lg:border-l lg:border-t-0 lg:pl-8 lg:pr-1">
            {hospitalCardActions(hospital, 'stack', detailNavigation)}
          </div>
        </div>
      </article>
    </li>
  )
}

const agencyCardActions = (agency: Agency, layout: 'row' | 'stack', detailNavigation: DirectoryDetailNavigation) => {
  const isRow = layout === 'row'
  const agencyDetailTo = {
    pathname: `/agencies/${agency.permalink}`,
    state: getDirectoryDetailLinkState(detailNavigation.directoryReturnTo),
  }

  return (
    <div
      className={isRow ? 'mt-4 flex gap-2 lg:hidden' : 'hidden w-full flex-col gap-2 lg:flex lg:w-[10rem] xl:w-[11rem]'}
    >
      <Link
        to={agencyDetailTo}
        onClick={detailNavigation.onNavigateToDetail}
        className={cardButtonLayoutClass(isRow, true)}
      >
        {formatBrowseDirectoryLabel(agency.reviewCount)}
      </Link>
      <Link to={`/review-agency/${agency.id}`} className={cardButtonLayoutClass(isRow, false)}>
        {WRITE_REVIEW_LABEL}
      </Link>
    </div>
  )
}

// TODO: add link for agency reviews subsection of agency detail page on read reviews (current site uses hashtag scroll)
const AgencyListItem = ({
  agency,
  detailNavigation,
}: {
  agency: Agency
  detailNavigation: DirectoryDetailNavigation
}) => {
  const agencyDetailTo = {
    pathname: `/agencies/${agency.permalink}`,
    state: getDirectoryDetailLinkState(detailNavigation.directoryReturnTo),
  }

  return (
    <li>
      <article className="rounded-2xl border border-gray-100/90 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.03] transition-all duration-200 hover:border-gray-200 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] sm:p-5 lg:p-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-0 lg:p-5">
          <div className="flex min-w-0 flex-1 items-start gap-4 sm:gap-5 lg:gap-6 lg:pr-8">
            <Link
              to={agencyDetailTo}
              onClick={detailNavigation.onNavigateToDetail}
              className="relative mt-0.5 shrink-0 self-start overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-gray-100 ring-1 ring-gray-100/80"
            >
              <div className="flex size-[4.5rem] items-center justify-center sm:size-24 lg:size-[6.25rem]">
                <img
                  className="size-[3.25rem] object-contain p-0.5 sm:size-16 lg:size-[5rem]"
                  src={agency.image ? agency.image : undefined}
                  alt=""
                />
              </div>
            </Link>

            <div className="flex min-w-0 flex-1 flex-col lg:py-0.5">
              <Link
                to={agencyDetailTo}
                onClick={detailNavigation.onNavigateToDetail}
                className="text-base font-semibold leading-snug text-graphite-grey visited:text-graphite-grey hover:text-royal-indigo focus-visible:rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-indigo/35 sm:text-lg"
              >
                <span className="line-clamp-2">{agency.name}</span>
              </Link>

              <div className="mt-2 space-y-1.5 text-xs text-gray-600 sm:text-sm">
                {agency.address ? (
                  <div className="flex items-start gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400 sm:h-4 sm:w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="min-w-0 leading-relaxed text-gray-700">{agency.address}</span>
                  </div>
                ) : null}
                {agency.website ? (
                  <div className="flex min-w-0 items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5 shrink-0 text-gray-400 sm:h-4 sm:w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      />
                    </svg>
                    <a
                      href={`${!agency.website.startsWith('http') ? 'http://' : ''}${agency.website}`}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate font-medium text-dark-blue hover:text-royal-indigo"
                    >
                      {agency.website}
                    </a>
                  </div>
                ) : null}
                {agency.phone ? (
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5 shrink-0 text-gray-400 sm:h-4 sm:w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <a
                      href={`tel:${agency.phone.replace(/\D/g, '')}`}
                      className="font-medium text-dark-blue hover:text-royal-indigo"
                    >
                      {agency.phone}
                    </a>
                  </div>
                ) : null}
                {agency.facebook ? (
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5 shrink-0 text-gray-400 sm:h-4 sm:w-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <a
                      href={`${!agency.facebook.startsWith('http') ? 'http://' : ''}${agency.facebook}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-dark-blue hover:text-royal-indigo"
                    >
                      Facebook
                    </a>
                  </div>
                ) : null}
              </div>

              {agency.rating > 0 && !hasNoReviews(agency.reviewCount) ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StarRating rating={agency.rating} size="sm" />
                  <span className="text-sm font-semibold tabular-nums text-graphite-grey">
                    {typeof agency.rating === 'number'
                      ? agency.rating.toFixed(1)
                      : Number(agency.rating || 0).toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({agency.reviewCount} review{agency.reviewCount && agency.reviewCount > 1 ? 's' : ''})
                  </span>
                </div>
              ) : hasNoReviews(agency.reviewCount) ? (
                <div className="mt-3">
                  <NoReviewsYetBadge />
                </div>
              ) : null}

              {agencyCardActions(agency, 'row', detailNavigation)}
            </div>
          </div>

          <div className="hidden border-gray-100 lg:flex lg:shrink-0 lg:flex-col lg:justify-center lg:border-l lg:border-t-0 lg:pl-8 lg:pr-1">
            {agencyCardActions(agency, 'stack', detailNavigation)}
          </div>
        </div>
      </article>
    </li>
  )
}

//-----------------------------------------------------------------------------
// MAIN COMPONENT
//-----------------------------------------------------------------------------

type HospitalsBrowseLocationState = { scrollToBrowse?: boolean }

type HospitalsDirectorySearchReturnState = {
  directorySearchTerm?: string
  directoryTab?: 'hospitals' | 'agencies'
  scrollToBrowse?: boolean
}

const Hospitals: React.FC = () => {
  const location = useLocation()
  const history = useHistory()
  const initialDirectorySearch = parseDirectorySearchFromLocation(location)
  const { isTokenReady, token } = useAuthToken()
  const narrowDirectorySearch = useNarrowDirectorySearchViewport()
  const [activeTab, setActiveTab] = useState<'hospitals' | 'agencies'>(initialDirectorySearch.tab)
  const prevActiveTabRef = useRef(activeTab)
  const skipTabLayoutResetRef = useRef(true)
  const searchFetchGenerationRef = useRef(0)
  const { addMissingModal, openAddMissingModal } = useAddMissingOrganizationModal()
  const [hospitalResults, setHospitalResults] = useState<Hospital[]>([])
  const [agencyResults, setAgencyResults] = useState<Agency[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [numberShowing, setNumberShowing] = useState(0)
  const [initialLoad, setInitialLoad] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(initialDirectorySearch.page)
  const [totalPages, setTotalPages] = useState(0)
  const [searchErrorMessage, setSearchErrorMessage] = useState('')
  const [term, setTerm] = useState(initialDirectorySearch.q)
  const searchStateParam = getHashParamValueFromUrl<string>('state')
  const initSearchState: string | undefined = useMemo(
    () =>
      searchStateParam
        ? Object.entries(USStates).find(
            ([eachStateName, eachStateCode]) =>
              searchStateParam.replace(' ', '').toLowerCase() === eachStateName.toLowerCase() ||
              searchStateParam.toLowerCase() === eachStateCode.toLowerCase(),
          )?.[0]
        : undefined,
    [searchStateParam],
  )
  const searchCityParam = getHashParamValueFromUrl<string>('city')
  const initSearchCity: string | undefined = searchCityParam || undefined
  const [cityStateSearchTerm] = useState([initSearchCity, initSearchState].filter(Boolean).join(', '))
  const [autoCompleteTerm, setAutoCompleteTerm] = useState('')
  const [autoCompleteResults, setAutoCompleteResults] = useState<Hospital[] | Agency[]>([])
  const [sortValue, setSortValue] = useState(initialDirectorySearch.sort)
  const searchResultContainerRef = useRef<HTMLDivElement>(null)

  const hospitalServiceRef = useRef(new HospitalService())
  const agencyServiceRef = useRef(new AgencyService())

  const directoryReturnTo = useMemo(
    () => buildDirectorySearchLocation({ page, q: term, sort: sortValue, tab: activeTab }),
    [term, sortValue, page, activeTab],
  )

  const detailNavigation = useMemo(
    (): DirectoryDetailNavigation => ({
      directoryReturnTo,
      onNavigateToDetail: saveDirectoryScrollPosition,
    }),
    [directoryReturnTo],
  )

  const replaceDirectorySearchUrl = (params: {
    page: number
    q: string
    sort: string
    tab: 'hospitals' | 'agencies'
  }) => {
    if (location.pathname !== '/hospitals') return

    const desired = buildDirectorySearchLocation(params)
    if (directorySearchLocationMatches(location, desired)) return

    history.replace(desired)
  }

  // Restore directory state when the URL changes (e.g. browser back from a detail page).
  useEffect(() => {
    if (location.pathname !== '/hospitals') return

    const parsed = parseDirectorySearchFromLocation(location)
    setTerm((prev) => (prev !== parsed.q ? parsed.q : prev))
    setSortValue((prev) => (prev !== parsed.sort ? parsed.sort : prev))
    setPage((prev) => (prev !== parsed.page ? parsed.page : prev))
    setActiveTab((prev) => (prev !== parsed.tab ? parsed.tab : prev))
  }, [location.pathname, location.search, location.hash])

  // Apply term and tab from dedicated directory search screen, then clear that state
  useEffect(() => {
    const s = location.state as HospitalsDirectorySearchReturnState | undefined
    if (!s || s.directorySearchTerm === undefined) return

    const tab = s.directoryTab === 'agencies' ? 'agencies' : 'hospitals'
    setTerm(s.directorySearchTerm)
    setPage(1)
    setActiveTab(tab)

    history.replace({
      ...buildDirectorySearchLocation({ page: 1, q: s.directorySearchTerm, sort: sortValue, tab }),
      state: s.scrollToBrowse ? { scrollToBrowse: true } : undefined,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hand off directory search once per navigation
  }, [location.key, location.state, history])

  // Clear scroll intent from history once handled (scrolling is done from TopNav via scrollToBrowseMainSection)
  useEffect(() => {
    const state = location.state as HospitalsBrowseLocationState | undefined
    if (!state?.scrollToBrowse) return
    const id = requestAnimationFrame(() => {
      history.replace({
        hash: location.hash,
        pathname: location.pathname,
        search: location.search,
        state: undefined,
      })
    })
    return () => cancelAnimationFrame(id)
  }, [location.key, history, location.pathname, location.search, location.hash])

  const setSortAndUpdatePage = (newSort: string) => {
    setSortValue(newSort)
    setPage(1)
    replaceDirectorySearchUrl({ page: 1, q: term, sort: newSort, tab: activeTab })
  }

  const setTermAndUpdatePage = (newTerm: string) => {
    setTerm(newTerm)
    setPage(1)
    replaceDirectorySearchUrl({ page: 1, q: newTerm, sort: sortValue, tab: activeTab })
  }

  const setPageAndUpdateUrl: React.Dispatch<React.SetStateAction<number>> = (value) => {
    const newPage = typeof value === 'function' ? value(page) : value
    setPage(newPage)
    replaceDirectorySearchUrl({ page: newPage, q: term, sort: sortValue, tab: activeTab })
  }

  const setActiveTabAndUpdatePage = (tab: 'hospitals' | 'agencies') => {
    setActiveTab(tab)
    setPage(1)
    replaceDirectorySearchUrl({ page: 1, q: term, sort: sortValue, tab })
  }

  useEffect(() => {
    setSearchErrorMessage('')
  }, [])

  // Before paint: clear stale totals/lists when tab changes so we never show hospital counts with "agencies" (or vice versa).
  useLayoutEffect(() => {
    if (skipTabLayoutResetRef.current) {
      skipTabLayoutResetRef.current = false
      return
    }
    setTotalResults(0)
    setNumberShowing(0)
    setTotalPages(0)
    setSearchErrorMessage('')
    setIsLoading(true)
    if (activeTab === 'agencies') {
      setHospitalResults([])
    } else {
      setAgencyResults([])
    }
  }, [activeTab])

  useEffect(() => {
    if (!isTokenReady) return

    const switchedTabOnly = prevActiveTabRef.current !== activeTab
    prevActiveTabRef.current = activeTab

    let cancelled = false
    const fetchGeneration = ++searchFetchGenerationRef.current
    setIsLoading(true)
    setSearchErrorMessage('')
    const displaySortValue = sortValue === 'mostReviewed' ? 'mostReviewed' : sortValue
    // Only apply a minimum-rating gate for the "Top Rated" filter.
    // "Most Reviewed" sorts by review count and must NOT filter by rating —
    // otherwise a highly-reviewed hospital drops off the list the moment its
    // average dips below 3.5.
    const minRating = sortValue === 'highRating' ? 3.5 : undefined

    if (activeTab === 'agencies') {
      // Agencies search
      agencyServiceRef.current
        .search({
          minRating,
          page,
          sort: displaySortValue,
          term,
        })
        .then((results) => {
          if (cancelled || fetchGeneration !== searchFetchGenerationRef.current) return
          setIsLoading(false)
          setInitialLoad(false)
          setAgencyResults(
            results.agencies?.map((agency) => ({
              ...agency,
              image:
                agency.image ||
                'https://scrubstr-wp-uploads.s3.us-east-2.amazonaws.com/2021/01/07212249/hospital-placeholder.png',
            })),
          )
          setTotalResults(results.totalResults)
          setNumberShowing(results.agencies.length)
          setTotalPages(results.totalPages)
          if (!initialLoad && !switchedTabOnly) {
            scrollToResults()
          }
        })
        .catch((err) => {
          if (cancelled || fetchGeneration !== searchFetchGenerationRef.current) return
          const message =
            err instanceof Error ? err.message : typeof err === 'string' ? err : 'Unable to load agencies.'
          setSearchErrorMessage(message)
          setIsLoading(false)
          if (!switchedTabOnly) {
            scrollToResults()
          }
        })
    } else {
      // Hospitals search
      const cityStateParts = cityStateSearchTerm?.split(',')
      const stateString = !cityStateParts.length ? undefined : cityStateParts[cityStateParts.length - 1].trim()
      const stateCode = stateString
        ? Object.entries(USStates).find(
            ([eachStateName, eachStateCode]) =>
              stateString.replace(' ', '').toLowerCase() === eachStateName.toLowerCase() ||
              stateString.toLowerCase() === eachStateCode.toLowerCase(),
          )?.[1]
        : undefined
      const city = cityStateParts.length > 1 ? cityStateParts.slice(0, -1).join(',').trim() : undefined

      addHashParamToUrl('state', stateCode || '')
      addHashParamToUrl('city', city || '')

      hospitalServiceRef.current
        .search({
          city,
          minRating,
          page,
          sort: displaySortValue,
          state: stateCode,
          term,
        })
        .then((results) => {
          if (cancelled || fetchGeneration !== searchFetchGenerationRef.current) return
          setIsLoading(false)
          setInitialLoad(false)
          setHospitalResults(
            results.hospitals?.map((hospital) => ({
              ...hospital,
              image:
                hospital.image ||
                'https://scrubstr-wp-uploads.s3.us-east-2.amazonaws.com/2021/01/07212249/hospital-placeholder.png',
            })),
          )
          setTotalResults(results.totalResults)
          setNumberShowing(results.hospitals.length)
          setTotalPages(results.totalPages)
          if (!initialLoad && !switchedTabOnly) {
            scrollToResults()
          }
        })
        .catch((err) => {
          if (cancelled || fetchGeneration !== searchFetchGenerationRef.current) return
          const message =
            err instanceof Error ? err.message : typeof err === 'string' ? err : 'Unable to load hospitals.'
          setSearchErrorMessage(message)
          console.error(err)
          setIsLoading(false)
          if (!switchedTabOnly) {
            scrollToResults()
          }
        })
    }

    return () => {
      cancelled = true
    }
  }, [page, cityStateSearchTerm, sortValue, term, activeTab, isTokenReady, token])

  useEffect(() => {
    if (!initialLoad) {
      if (activeTab === 'agencies') {
        agencyServiceRef.current
          .search({ autocomplete: true, page: 1, sort: 'az', term: autoCompleteTerm })
          .then((results) => {
            setAutoCompleteResults(results.agencies)
          })
          .catch(() => {
            // likely just a cancel of a previous request
          })
      } else {
        const cityStateParts = cityStateSearchTerm?.split(',')
        const stateString = !cityStateParts.length ? undefined : cityStateParts[cityStateParts.length - 1].trim()
        const stateCode = stateString
          ? Object.entries(USStates).find(
              ([eachStateName, eachStateCode]) =>
                stateString.replace(' ', '').toLowerCase() === eachStateName.toLowerCase() ||
                stateString.toLowerCase() === eachStateCode.toLowerCase(),
            )?.[1]
          : undefined
        const city = cityStateParts.length > 1 ? cityStateParts.slice(0, -1).join(',').trim() : undefined

        hospitalServiceRef.current
          .search({ autocomplete: true, city, page: 1, sort: 'az', state: stateCode, term: autoCompleteTerm })
          .then((results) => {
            setAutoCompleteResults(results.hospitals)
          })
          .catch(() => {
            // likely just a cancel of a previous request
          })
      }
    } else {
      setAutoCompleteResults([])
    }
  }, [autoCompleteTerm, activeTab, cityStateSearchTerm, initialLoad])

  useEffect(() => {
    if (isLoading || initialLoad) return

    const scrollY = consumeDirectoryScrollRestore()
    if (scrollY === null) return

    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY)
    })
  }, [isLoading, initialLoad, hospitalResults, agencyResults])

  const scrollToResults = () => {
    if (searchResultContainerRef && searchResultContainerRef.current)
      scrollIntoView(searchResultContainerRef.current, 0)
  }

  return (
    <div id="page-root" className="h-screen overflow-y-auto overflow-x-hidden relative w-full max-w-full bg-white">
      <TopLogo />

      <section
        id="browse-main"
        className="relative z-10 scroll-mt-[calc(var(--safe-area-top)+5rem)] bg-aqua-mint px-4 pb-10 pt-6 text-white md:pb-14 md:pt-10"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="mb-3 px-4 py-2 text-2xl font-bold leading-tight sm:mb-4 sm:px-6 sm:py-2.5 sm:text-3xl md:px-8 md:py-3 md:text-4xl">
            Reviews from the breakroom, not the boardroom.
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed px-2">
            See what healthcare workers are really saying about hospitals and agencies across the country.
          </p>

          <div className="flex justify-center mb-5 md:mb-6 px-2">
            <div
              className="inline-flex bg-black/10 rounded-full p-1 gap-1 max-w-full overflow-x-auto"
              role="tablist"
              aria-label="Search category"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'hospitals'}
                onClick={() => setActiveTabAndUpdatePage('hospitals')}
                className={`px-6 sm:px-10 py-2.5 rounded-full font-semibold text-sm sm:text-base transition-all min-w-[108px] sm:min-w-[140px] flex-shrink-0 ${
                  activeTab === 'hospitals' ? 'bg-orange text-white shadow-md' : 'text-white/90 hover:bg-white/10'
                }`}
              >
                Hospitals
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'agencies'}
                onClick={() => setActiveTabAndUpdatePage('agencies')}
                className={`px-6 sm:px-10 py-2.5 rounded-full font-semibold text-sm sm:text-base transition-all min-w-[108px] sm:min-w-[140px] flex-shrink-0 ${
                  activeTab === 'agencies' ? 'bg-orange text-white shadow-md' : 'text-white/90 hover:bg-white/10'
                }`}
              >
                Agencies
              </button>
            </div>
          </div>

          {narrowDirectorySearch ? (
            <div className="relative z-20 mx-auto w-full max-w-2xl">
              <button
                type="button"
                className="flex w-full min-h-[52px] flex-row items-center rounded-full bg-white p-1.5 pl-4 text-left shadow-[0_4px_28px_-6px_rgba(0,0,0,0.14)] ring-1 ring-black/[0.06] sm:pl-6"
                onClick={() =>
                  history.push({
                    pathname: '/directory-search',
                    search: `?tab=${activeTab}`,
                    state: {
                      cityStateSearchTerm,
                      initialQuery: term,
                    } as DirectorySearchLocationState,
                  })
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mr-2 h-5 w-5 shrink-0 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
                <span
                  className={`min-w-0 flex-1 truncate py-2.5 pr-2 text-base sm:text-[17px] ${term ? 'text-graphite-grey' : 'text-gray-500'}`}
                >
                  {term || 'Search hospitals or agencies…'}
                </span>
                <span className="mr-3 shrink-0 text-sm font-semibold text-orange">Search</span>
              </button>
            </div>
          ) : (
            <Filter
              currentSearchTerm={term}
              setNewSearchTerm={setTermAndUpdatePage}
              setAutoCompleteTerm={setAutoCompleteTerm}
              autoCompleteResults={autoCompleteResults}
              activeTab={activeTab}
            />
          )}

          <div className="mt-8 md:mt-10 max-w-lg mx-auto border-t border-white/25 pt-6 md:pt-8 grid grid-cols-3 divide-x divide-white/25">
            <div className="text-center px-2">
              <div className="text-xl md:text-2xl font-bold tabular-nums">7,400+</div>
              <div className="text-xs md:text-sm text-white/85 mt-0.5">Hospitals</div>
            </div>
            <div className="text-center px-2">
              <div className="text-xl md:text-2xl font-bold tabular-nums">146</div>
              <div className="mt-0.5 text-xs text-white/85 md:text-sm">Agencies</div>
            </div>
            <div className="text-center px-2">
              <div className="text-xl md:text-2xl font-bold tabular-nums">100%</div>
              <div className="text-xs md:text-sm text-white/85 mt-0.5">Anonymous</div>
            </div>
          </div>
        </div>
      </section>

      <div className="relative z-0 px-4 pt-8 pb-4 max-w-xl lg:max-w-6xl mx-auto">
        <RecentReviewsShowcase />

        <div className="search-results" ref={searchResultContainerRef}>
          {isLoading && initialLoad && (
            <div className="flex justify-center items-center py-20">
              <Spinner size="lg" />
            </div>
          )}

          {!isLoading && (
            <div className="mx-auto w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl">
              <div className="mt-10 overflow-hidden rounded-2xl border border-gray-100/90 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.03] sm:mt-12">
                <div className="border-b border-gray-100/90 bg-gradient-to-b from-slate-50/80 to-slate-50/40 px-4 py-4 sm:px-5 sm:py-5">
                  <Sort
                    totalResults={totalResults}
                    sortValue={sortValue}
                    setSortValue={setSortAndUpdatePage}
                    numberShowing={numberShowing}
                    page={page}
                    resultCategory={activeTab === 'hospitals' ? 'hospitals' : 'agencies'}
                  />
                </div>

                {searchErrorMessage.length > 0 && (
                  <div className="border-b border-gray-100/90 px-4 py-4 sm:px-5">
                    <ErrorView>{searchErrorMessage}</ErrorView>
                  </div>
                )}

                {activeTab === 'hospitals' && (
                  <>
                    {!(hospitalResults.length > 0) && (
                      <div className="px-4 py-12 text-center sm:px-5">
                        <div className="mb-4 text-lg text-gray-600">No hospitals found matching your search.</div>
                        <button
                          onClick={() => openAddMissingModal('hospital')}
                          className="inline-block rounded-xl bg-orange px-8 py-3 font-semibold text-white shadow-sm transition-all hover:bg-opacity-90"
                        >
                          Add Missing Hospital
                        </button>
                      </div>
                    )}
                    {hospitalResults.length > 0 && (
                      <ul className="flex flex-col gap-3 bg-slate-50/25 p-4 sm:gap-4 sm:p-5">
                        {hospitalResults.map((hospital) => (
                          <HospitalListItem key={hospital.id} hospital={hospital} detailNavigation={detailNavigation} />
                        ))}
                      </ul>
                    )}
                  </>
                )}

                {activeTab === 'agencies' && (
                  <>
                    {!(agencyResults.length > 0) && (
                      <div className="px-4 py-12 text-center sm:px-5">
                        <div className="mb-4 text-lg text-gray-600">No agencies found matching your search.</div>
                        <button
                          onClick={() => openAddMissingModal('agency')}
                          className="inline-block rounded-xl bg-orange px-8 py-3 font-semibold text-white shadow-sm transition-all hover:bg-opacity-90"
                        >
                          Add Missing Agency
                        </button>
                      </div>
                    )}
                    {agencyResults.length > 0 && (
                      <ul className="flex flex-col gap-3 bg-slate-50/25 p-4 sm:gap-4 sm:p-5">
                        {agencyResults.map((agency) => (
                          <AgencyListItem key={agency.id} agency={agency} detailNavigation={detailNavigation} />
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>

              {totalPages > 1 && (
                <Paging
                  // className="mt-10 border-t border-gray-100/90 pt-8"
                  totalPages={totalPages}
                  currentPage={page}
                  setPage={setPageAndUpdateUrl}
                  scrollToResults={scrollToResults}
                />
              )}
            </div>
          )}

          {isLoading && !initialLoad && (
            <div className="flex justify-center items-center py-12">
              <Spinner size="md" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-12">
        <Footer />
      </div>
      {addMissingModal}
    </div>
  )
}

export default Hospitals
